import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verify token on mount
  const verifyToken = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('admin_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      const data = response.data;

      // 2FA challenge: don't set session yet, hand pending token back to caller
      if (data.requires_2fa) {
        return { success: false, requires2fa: true, pendingToken: data.pending_token };
      }

      localStorage.setItem('admin_token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const verify2fa = async (pendingToken, code) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/2fa/verify`, {
        pending_token: pendingToken,
        code
      });
      const data = response.data;
      localStorage.setItem('admin_token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || '2FA verification failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    // Best-effort: record the logout event before clearing the token
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      // Ignore logout logging failures; still clear local session
    }
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  };

  const hasRole = (allowedRoles) => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };

  const isOwner = () => hasRole(['owner']);
  const isAdmin = () => hasRole(['owner', 'admin']);
  const isAnyAdmin = () => hasRole(['owner', 'admin', 'outreach']);

  const value = {
    user,
    token,
    loading,
    login,
    verify2fa,
    logout,
    hasRole,
    isOwner,
    isAdmin,
    isAnyAdmin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
